/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { memo } from "react";
import { ReactFlowProvider, Handle, Position } from "reactflow";
import { LinearProgress, Typography, Box } from "@mui/material";
import { assemblyComponent, loadChilds } from "../Logic/formatUtils";
import PieChartNode from "./PieChartNode";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import AlarmBadge from "../Common/AlarmBadge";
import { getDiagnosticAlarm, getStatusAlarm } from "../../../lib/AlarmUtil";

const CustomNode = memo(({ data }) => {
  const theme = useTheme();

  const getBadgeCount = (alarms) => {
    if (!alarms) return 0;
    return alarms.length;
  };

  const statusAlarm = getStatusAlarm(data?.alarms);

  /* DER color rules:
        if zero power, text color is dark grey
        if positive power, text color is power color
        if negative power, text color is BESS color
    */
  const getDERColor = (power, allowNegative) => {
    if (statusAlarm || power === null) return theme.palette.grey.light;
    if (power > 0) return theme.palette.power.main;
    if (power < 0 && allowNegative)
      return allowNegative ? theme.palette.bess.main : theme.palette.power.main;
    if (power === 0) return theme.palette.grey.dark;
  };

  const getPowerColor = (power, allowNegative) => {
    if (statusAlarm || power === null) return theme.palette.error.main;
    if (power > 0) return theme.palette.power.main;
    if (power < 0 && allowNegative)
      return allowNegative ? theme.palette.bess.main : theme.palette.power.main;
    if (power === 0) return theme.palette.grey.dark;
  };

  const getPercentageDisplay = (data) => {
    let pctDisplay = "--";
    let pctVal = Math.round(data?.source.toLowerCase() === "bess" ? data.soc : data.percentage);
    if (!isNaN(pctVal)) {
      pctDisplay = pctVal + "%";
    }
    return pctDisplay;
  };

  let isChild = false;
  const AssemblyComponent = assemblyComponent[data?.source.replace(/ \d+$/, "").toLowerCase()];
  if (data.nodeType === "square") {
    isChild = loadChilds.includes(data.source.replace(/ \d+$/, ""));
  }

  const derColor = getDERColor(data.power, Object.hasOwn(data, "soc"));
  const powerColor = getPowerColor(data.power, Object.hasOwn(data, "soc"));
  const nameColor =
    statusAlarm || data.power === null ? theme.palette.grey.main : theme.palette.grey.dark;
  const barColor = statusAlarm || data.power === null ? "lightGrey" : "grey";
  const loadPX = data?.source.toLowerCase() === "load" ? "2" : "1";
  const borderNode = `${loadPX}px solid ${nameColor}`;
  const unavailableLabel = statusAlarm?.type === "diagnostic" ? "Disconnected" : "Unavailable";

  const styles = {
    badge: {
      position: "absolute",
      top: "-16px",
      right: "0",
    },
  };

  return (
    <Box>
      <Box sx={styles.badge}>
        <AlarmBadge
          alarmsCount={data.alarmsCount}
          alarms={data.alarms}
          timezone={data.timezone}
          badgeProps={{
            invisible:
              !(data.source !== "utility-pie" && data.source !== "load-pie") ||
              getBadgeCount(data.alarms) === 0,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          }}
          source={data.source}
          onShowAlarmModal={data.onShowAlarmModal}
        ></AlarmBadge>
      </Box>
      <div
        className={data.source !== "utility-pie" && data.source !== "load-pie" ? "custom-node" : ""}
        style={{
          "--border-custom-node": borderNode,
          "--custom-left": isChild ? "9px" : "0px",
          background: "#fff",
          boxShadow:
            data.source !== "utility-pie" && data.source !== "load-pie"
              ? "3px 3px 10px rgba(0, 0, 0, 0.1)"
              : null,
        }}
      >
        <ReactFlowProvider>
          <Handle type="target" position={Position.Left} id="left" style={{ background: "#fff" }} />
          {data.source === "utility-pie" || data.source === "load-pie" ? (
            <PieChartNode value={data.percentage} />
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems={data?.source === "Load" || data.source === "Utility" ? "center" : "left"}
              height="100%"
            >
              {isChild && (
                <AssemblyComponent
                  className="assembly-icon"
                  style={{
                    "--assembly-icon-color": derColor,
                  }}
                />
              )}
              <Box className="node-source" style={{ "--node-source-color": nameColor }}>
                {data?.name}
              </Box>
              <Box className="node-data" style={{ textAlign: "left" }}>
                <span style={{ color: powerColor }}>
                  {statusAlarm || data.power === null
                    ? unavailableLabel
                    : `${Math.round(data?.power)}kW`}
                </span>
              </Box>
              {isChild && (
                <div style={{ position: "relative" }}>
                  <LinearProgress
                    color={barColor}
                    value={
                      (data?.source.toLowerCase() === "bess" ? data.soc : data.percentage) || 0
                    }
                    sx={{
                      height: "12px",
                      "--node-source-color": powerColor,
                    }}
                    variant="determinate"
                  />
                  <Typography
                    sx={{
                      position: "absolute",
                      color: nameColor,
                      top: 0,
                      left: "5%",
                      transform: "translate(-10%, -11%)",
                    }}
                    variant="caption"
                  >
                    {data?.source.toLowerCase() === "bess" ? "SOC:" : "CF:"}
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      color: derColor,
                      top: 0,
                      left: "95%",
                      transform: "translate(-95%, -11%)",
                    }}
                    variant="caption"
                  >
                    {getPercentageDisplay(data)}
                  </Typography>
                </div>
              )}
            </Box>
          )}
          <Handle
            type="source"
            position={Position.Right}
            id="right"
            style={{ background: "#fff" }}
          />
        </ReactFlowProvider>
      </div>
    </Box>
  );
});

CustomNode.propTypes = {
  data: PropTypes.object.isRequired,
};
CustomNode.displayName = "CustomNode";

export default CustomNode;
